import { AuthService } from './authService';
import { extractApiError } from '../utils/apiError';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

/**
 * Fellesmaler-modulen: admin-CRUD mot master WMS_SharedReports via
 * SharedReportsAdminController. Endringer treffer ALLE tenants i miljøet.
 * Backend håndhever tilgang (moduleAccess.sharedReportDesigner + admin-gruppe).
 */

export interface SharedReport {
    reportID: number;
    reportType: number;
    reportName: string;
    description?: string | null;
    externalPrinterWidth?: number | null;
    externalPrinterLength?: number | null;
    externalPrinterLandscape?: boolean | null;
    layoutJson: string;
    createdBy?: string | null;
    created?: string | null;
    updatedBy?: string | null;
    lastUpdate?: string | null;
}

export interface SaveSharedReportRequest {
    reportType: number;
    reportName: string;
    description?: string | null;
    externalPrinterWidth?: number | null;
    externalPrinterLength?: number | null;
    externalPrinterLandscape?: boolean | null;
    layoutJson: string;
}

export interface SharedReportHistoryEntry {
    id: number;
    reportID: number;
    reportType: number;
    reportName: string;
    description?: string | null;
    externalPrinterWidth?: number | null;
    externalPrinterLength?: number | null;
    externalPrinterLandscape?: boolean | null;
    layoutJson: string;
    changeType: string;
    changedBy?: string | null;
    changed: string;
}

class SharedReportsAdminService {
    private base(companyNo: string): string {
        return `${API_BASE_URL}/${companyNo}/shared-reports-admin`;
    }

    async getAll(companyNo: string): Promise<SharedReport[]> {
        const response = await AuthService.authenticatedGet(this.base(companyNo));
        if (!response.ok) {
            const error = await response.json().catch(() => null);
            throw new Error(extractApiError(error, response, 'Kunne ikke hente fellesmaler'));
        }
        return response.json();
    }

    async update(companyNo: string, reportId: number, request: SaveSharedReportRequest): Promise<void> {
        const response = await AuthService.authenticatedPut(
            `${this.base(companyNo)}/${reportId}`, request);
        if (!response.ok) {
            const error = await response.json().catch(() => null);
            throw new Error(extractApiError(error, response, 'Kunne ikke lagre fellesmalen'));
        }
    }

    async create(companyNo: string, reportId: number, request: SaveSharedReportRequest): Promise<void> {
        const response = await AuthService.authenticatedPost(
            `${this.base(companyNo)}/${reportId}`, request);
        if (!response.ok) {
            const error = await response.json().catch(() => null);
            throw new Error(extractApiError(error, response, 'Kunne ikke opprette fellesmalen'));
        }
    }

    async getHistory(companyNo: string, reportId: number): Promise<SharedReportHistoryEntry[]> {
        const response = await AuthService.authenticatedGet(
            `${this.base(companyNo)}/${reportId}/history`);
        if (!response.ok) {
            const error = await response.json().catch(() => null);
            throw new Error(extractApiError(error, response, 'Kunne ikke hente historikk'));
        }
        return response.json();
    }
}

const sharedReportsAdminService = new SharedReportsAdminService();
export default sharedReportsAdminService;
